import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  calculateCoverage,
  createMaterials,
  formatDate,
  formatQuantity,
  getDate,
  getDaysDifference,
  getMaterial,
  getMaterialResult,
  getPRStatus,
  getRemainingPRQuantity,
  getPOStatus,
  getPOBalance,
  normalizeMaterial,
} from "../../utils/reportUtils";
import "../../App.css";
const API = process.env.REACT_APP_BACKEND_URL;
const MaterialDetail = ({ materialKey, onClose }) => {
  const { key: urlKey } = useParams();
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const authHeader = {
    headers: { Authorization: `Bearer ${storedUser?.accessToken}` },
  };
  // Same material key as the coverage lists (case, spaces and leading zeros ignored)
  const key = normalizeMaterial(materialKey || urlKey);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mrp, setMrp] = useState([]);
  const [stock, setStock] = useState([]);
  const [prs, setPrs] = useState([]);
  const [pos, setPos] = useState([]);
  const [productionAdjustments, setProductionAdjustments] = useState([]);
  const [partMasters, setPartMasters] = useState([]);
  const [allottedRequirements, setAllottedRequirements] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [latestUpload, setLatestUpload] = useState(null);
  // India date, not UTC, so "today" is correct between 00:00 and 05:30 IST.
  const today = getDate(new Date());
  const planningMonth = today.slice(0, 7);
  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [key]);
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, s, p, o, prd, pm, alt, d, u] = await Promise.all([
        axios.get(`${API}/mrp/latest`, authHeader),
        axios.get(`${API}/stock/latest`, authHeader),
        axios.get(`${API}/open-pr/latest`, authHeader),
        axios.get(`${API}/open-po/latest`, authHeader),
        axios.get(`${API}/production-report/inventory-adjustment`, authHeader),
        axios.get(`${API}/part-master`, authHeader),
        axios.get(
          `${API}/production-allocation/material-requirement?month=${planningMonth}`,
          authHeader,
        ),
        // Receipts after the stock date are added to stock (same as the list pages).
        axios
          .get(`${API}/delivery-report/latest`, authHeader)
          .catch(() => ({ data: [] })),
        axios
          .get(`${API}/report-upload/latest`, authHeader)
          .catch(() => ({ data: null })),
      ]);
      setMrp(m.data || []);
      setStock(s.data || []);
      setPrs(p.data || []);
      setPos(o.data || []);
      setProductionAdjustments(prd.data || []);
      setPartMasters(pm.data || []);
      setAllottedRequirements(alt.data || []);
      setDeliveries(d.data || []);
      setLatestUpload(u.data || null);
    } catch (e) {
      setError(
        e.response?.data?.Exception ||
          e.response?.data?.message ||
          "Could not load material.",
      );
    } finally {
      setLoading(false);
    }
  };
  const getCoverageClass = (value) => {
    if (value === "SHORT") {
      return "b-short";
    }
    if (value === "AT_RISK") {
      return "b-risk";
    }
    return "b-ok";
  };
  const getCoverageText = (value) => {
    if (value === "SHORT") {
      return "Short";
    }
    if (value === "AT_RISK") {
      return "At risk";
    }
    return "Covered";
  };
  const getTypeLabel = (type) => {
    if (type === "STOCK") {
      return "On hand";
    }
    if (type === "RECEIPT") {
      return "Received";
    }
    if (type === "OPEN_PO") {
      return "PO";
    }
    if (type === "OPEN_PR") {
      return "PR";
    }
    if (type === "REQUIREMENT") {
      return "Allotted";
    }
    // Today's production adds stock; it is not demand.
    if (type === "PRODUCTION") {
      return "Production";
    }
    return "Demand";
  };
  const getTypeClass = (type) => {
    if (type === "STOCK" || type === "RECEIPT" || type === "PRODUCTION") {
      return "b-grey";
    }
    if (type === "OPEN_PO") {
      return "b-info";
    }
    if (type === "OPEN_PR") {
      return "b-risk";
    }
    return "b-out";
  };
  const getPRBadgeClass = (value) => {
    return value === "CLOSED" ? "b-grey" : "b-info";
  };

  const getPRBadgeText = (value) => {
    return value === "CLOSED" ? "Closed" : "Open";
  };
  const getPOBadgeClass = (value) => {
    if (value === "CLOSED") {
      return "b-ok";
    }
    if (value === "OVERDUE") {
      return "b-short";
    }
    if (value === "DUE_SOON") {
      return "b-risk";
    }
    return "b-grey";
  };
  const getPOBadgeText = (value) => {
    if (value === "CLOSED") {
      return "Closed";
    }
    if (value === "OVERDUE") {
      return "Overdue";
    }
    if (value === "DUE_SOON") {
      return "Due soon";
    }
    return "Open";
  };
  if (loading) {
    return (
      <div className="page">
        <div className="empty">Loading material...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="page">
        <div className="note err">
          <b>Couldn't load data.</b> {error}
        </div>
      </div>
    );
  }
  // const materials = createMaterials(mrp, stock, prs, pos);
  // const results = calculateCoverage(mrp, stock, prs, pos, today);
  // const material = getMaterial(materials, key);
  // const result = getMaterialResult(results, key);
  const materialMrp = mrp.filter(
    (item) => normalizeMaterial(item.material) === normalizeMaterial(key),
  );
  const materialStock = stock.filter(
    (item) => normalizeMaterial(item.material) === normalizeMaterial(key),
  );
  const materialPrs = prs.filter(
    (item) => normalizeMaterial(item.material) === normalizeMaterial(key),
  );
  const materialPos = pos.filter(
    (item) => normalizeMaterial(item.material) === normalizeMaterial(key),
  );
  const materialProductionAdjustments = productionAdjustments.filter(
    (item) => normalizeMaterial(item.material) === normalizeMaterial(key),
  );
  // Requirement is the planner's allotted quantity from this month onwards, not MRP.
  const materialAllotments = allottedRequirements.filter(
    (item) => normalizeMaterial(item.material) === normalizeMaterial(key),
  );
  const formatMonth = (month) =>
    new Date(`${month}-01T00:00:00`).toLocaleString("en-IN", {
      month: "short",
      year: "numeric",
    });
  const allottedMonths = materialAllotments.map((item) => item.month).sort();
  const firstAllottedMonth = allottedMonths[0] || planningMonth;
  const lastAllottedMonth =
    allottedMonths[allottedMonths.length - 1] || planningMonth;
  const allottedPeriod =
    firstAllottedMonth === lastAllottedMonth
      ? formatMonth(firstAllottedMonth)
      : `${formatMonth(firstAllottedMonth)} – ${formatMonth(lastAllottedMonth)}`;
  const materials = createMaterials(
    materialMrp,
    materialStock,
    materialPrs,
    materialPos,
    materialProductionAdjustments,
  );
  // Stock date of the whole stock file (fallback: its upload date), so a material without its own
  // Stock Date uses the same cut-off as on the list pages.
  const fileStockDate =
    stock
      .map((item) => getDate(item.stockDate))
      .filter(Boolean)
      .sort()
      .pop() ||
    latestUpload?.uploadDate ||
    latestUpload?.createdDate;
  const results = calculateCoverage(
    materialMrp,
    materialStock,
    materialPrs,
    materialPos,
    today,
    materialProductionAdjustments,
    materialAllotments,
    deliveries.filter((item) => normalizeMaterial(item.material) === normalizeMaterial(key)),
    fileStockDate,
    latestUpload?.uploadDate || latestUpload?.createdDate,
  );
  const material = getMaterial(materials, key);
  const result = getMaterialResult(results, key);
  if (!material || !result) {
    return (
      <div className="page">
        <div className="empty">
          <strong>Material not found.</strong>
          <div style={{ marginTop: 14 }}>
            <button
              className="btn btn-p"
              onClick={() => navigate("../shortages")}
            >
              Back to shortage workbench
            </button>
          </div>
        </div>
      </div>
    );
  }
  const maxAbs = Math.max(
    ...result.timeline.map((t) => Math.abs(t.balance)),
    1,
  );
  // const materialPrs = prs.filter(
  //   (p) => String(p.material || "").trim() === material.key,
  // );
  // const materialPos = pos.filter(
  //   (o) => String(o.material || "").trim() === material.key,
  // );
  // The PO of a PR, found the same way its "Closed" status is: the PO number on the PR,
  // or a PO that links back to the PR (item, material and plant compared only when both are known)
  const getLinkedPo = (pr) => {
    const text = (value) => String(value || "").trim().toUpperCase();
    const matches = (a, b) => !text(a) || !text(b) || text(a) === text(b);
    if (text(pr.purchaseOrder)) {
      return pos.find(
        (po) =>
          text(po.purchasingDocument) === text(pr.purchaseOrder) &&
          matches(po.material, pr.material),
      ) || { purchasingDocument: pr.purchaseOrder };
    }
    return pos.find(
      (po) =>
        text(po.purchaseRequisition) === text(pr.purchaseRequisition) &&
        matches(po.requisitionItem, pr.item || pr.itemOfRequisition) &&
        matches(po.material, pr.material) &&
        matches(po.plant, pr.plant),
    );
  };
  const partMaster = partMasters.find(
    (item) => normalizeMaterial(item?.partCode) === normalizeMaterial(key),
  );
  const leadTimeDays = Number(partMaster?.leadTimeDays || 0);
  const getProcurementAlert = () => {
    if (!result.firstShortageDate || leadTimeDays <= 0) {
      return null;
    }
    const shortageDate = new Date(`${result.firstShortageDate}T00:00:00`);
    if (Number.isNaN(shortageDate.getTime())) {
      return null;
    }
    const alertStartDate = new Date(shortageDate);
    // 15 Oct shortage with 15 day lead time starts alerting on 1 Oct.
    alertStartDate.setDate(alertStartDate.getDate() - leadTimeDays + 1);
    const currentDate = new Date(`${today}T00:00:00`);
    if (Number.isNaN(currentDate.getTime()) || currentDate < alertStartDate) {
      return null;
    }
    // A line closed in SAP has no open quantity
    const hasOpenPo = materialPos.some(
      (po) => getPOBalance(po) > 0 && getPOStatus(po, today) !== "CLOSED",
    );
    // Show the alert whenever a material has no open PO.
    // An OPEN PR is still waiting for a PO, so it must not hide this alert.
    if (hasOpenPo) {
      return null;
    }
    const daysUntilShortage = Math.ceil(
      (shortageDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24),
    );
    const todayOrderDate = new Date(`${today}T00:00:00`);
    todayOrderDate.setDate(todayOrderDate.getDate() + leadTimeDays);

    // Format the local date directly; toISOString() shifts IST midnight back a day.
    const expectedDeliveryDate = formatDate(todayOrderDate);
    if (daysUntilShortage < 0) {
      return {
        type: "err",
        title: "Procurement overdue",
        message: `The first shortage was on ${formatDate(
          result.firstShortageDate,
        )}. No open PO is available. Lead time is ${leadTimeDays} days.`,
        expectedDeliveryDate,
        daysUntilShortage,
      };
    }
    return {
      type: "warn",
      title: "Procurement action required",
      message: `First shortage is on ${formatDate(
        result.firstShortageDate,
      )}. Lead time is ${leadTimeDays} days and no open PO is available.`,
      daysUntilShortage,
      expectedDeliveryDate,
      daysUntilShortage,
    };
  };
  const procurementAlert = getProcurementAlert();
  return (
    <div className="page">
      <div className="crumb">
        <button
          className="lnk"
          onClick={() => {
            if (onClose) {
              onClose();
            } else {
              navigate("../shortages");
            }
          }}
        >
          ← {onClose ? "Close" : "Shortage workbench"}
        </button>
        &nbsp;/ {material.desc}
      </div>
      <div className="ph">
        <div>
          <h1>{material.desc}</h1>
          <p>
            <span className="mono">{material.code}</span> ·{" "}
            {material.materialGroup} · base unit {material.uom} · plant{" "}
            {material.plant}
          </p>
        </div>
        <div>
          <span className={`bg ${getCoverageClass(result.status)}`}>
            {getCoverageText(result.status)}
          </span>
        </div>
      </div>
      <div className="tiles">
        <button className="tile t-mut">
          <div className="tile-l">Total requirement</div>
          <div className="tile-v">
            {formatQuantity(result.totalRequirement)}
          </div>
          <div className="tile-s">
            {material.uom} allotted for {allottedPeriod}
          </div>
        </button>
        <button className="tile t-info">
          <div className="tile-l">Total supply</div>
          <div className="tile-v">{formatQuantity(result.totalSupply)}</div>
          <div className="tile-s">stock + PR + PO</div>
        </button>
        <button
          className={`tile ${result.finalBalance < 0 ? "t-short" : "t-ok"}`}
        >
          <div className="tile-l">Closing balance</div>
          <div className="tile-v">{formatQuantity(result.finalBalance)}</div>
          <div className="tile-s">end of horizon</div>
        </button>
        <button
          className={`tile ${result.firstShortageDate ? "t-risk" : "t-ok"}`}
        >
          <div className="tile-l">First shortage</div>
          <div className="tile-v">
            {result.firstShortageDate
              ? formatDate(result.firstShortageDate)
              : "None"}
          </div>
          <div className="tile-s">
            {result.firstShortageDate
              ? `in ${getDaysDifference(result.firstShortageDate, today)} days`
              : "balance stays positive"}
          </div>
        </button>
      </div>
      {procurementAlert && (
        <div
          className={`note ${procurementAlert.type === "err" ? "err" : "warn"}`}
          style={{ marginBottom: 14 }}
        >
          <b>{procurementAlert.title}.</b> {procurementAlert.message}
          {procurementAlert.daysUntilShortage !== undefined && (
            <>
              {" "}
              There are only <b>
                {procurementAlert.daysUntilShortage} days
              </b>{" "}
              remaining until the shortage.
            </>
          )}
          {procurementAlert.expectedDeliveryDate && (
            <>
              {" "}
              If ordered today, the expected availability date is{" "}
              <b>{procurementAlert.expectedDeliveryDate}</b>.
            </>
          )}
        </div>
      )}
      {/* {result.status === "AT_RISK" && (
        <div className="note warn" style={{ marginBottom: 14 }}>
          <b>Quantity is sufficient, timing is not.</b> Total supply covers total demand, but the balance turns negative on {formatDate(result.firstShortageDate)} because supply lands after it is needed. Pull the delivery forward rather than ordering more.
        </div>
      )}
      {result.status === "SHORT" && (
        <div className="note err" style={{ marginBottom: 14 }}>
          <b>Genuinely short by {formatQuantity(result.shortageQty)} {material.uom}.</b> Even with everything already on order, the balance ends negative. A new requisition is needed.
        </div>
      )} */}
      <div className="card">
        <div className="card-h">
          <div>
            <div className="card-t">Stock and requirements timeline</div>
            <div className="card-s">
              Every supply and demand event in date order, with the running
              balance after each
            </div>
          </div>
        </div>
        <div className="tw">
          <table className="dt">
            <thead>
              <tr>
                <th></th>
                <th>Date</th>
                <th>Type</th>
                <th>Reference</th>
                <th className="r">In</th>
                <th className="r">Out</th>
                <th className="r" style={{ width: 210 }}>
                  Running balance
                </th>
              </tr>
            </thead>
            <tbody>
              {result.timeline.map((t) => {
                const width = Math.min(50, (Math.abs(t.balance) / maxAbs) * 50);
                const negative = t.balance < 0;
                return (
                  <tr key={t.seq} className={negative ? "tl-row-neg" : ""}>
                    <td className="faint mono r" style={{ width: 34 }}>
                      {t.seq}
                    </td>
                    <td>{formatDate(t.date)}</td>
                    <td>
                      <span className={`bg ${getTypeClass(t.type)}`}>
                        {getTypeLabel(t.type)}
                      </span>
                    </td>
                    <td className="mono dim">{t.ref}</td>
                    <td className="num pos">
                      {t.inQ > 0 ? `+${formatQuantity(t.inQ)}` : ""}
                    </td>
                    <td className="num neg">
                      {t.outQ > 0 ? `−${formatQuantity(t.outQ)}` : ""}
                    </td>
                    <td>
                      <div className="tl-bal">
                        <span className={`num ${negative ? "neg" : ""}`}>
                          {formatQuantity(t.balance)}
                        </span>
                        <span className="tl-bar">
                          <span className="mid"></span>
                          <span
                            className={`fill ${negative ? "n" : "p"}`}
                            style={{ width: `${width}%` }}
                          ></span>
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="grid2">
        <div className="card">
          <div className="card-h">
            <div className="card-t">Purchase requisitions</div>
          </div>
          <div className="tw">
            <table className="dt">
              <thead>
                <tr>
                  <th>PR</th>
                  <th className="r">Qty</th>
                  <th>Required</th>
                  <th>Status</th>
                  <th>PO</th>
                </tr>
              </thead>
              <tbody>
                {materialPrs.length > 0 ? (
                  materialPrs.map((p) => {
                    const status = getPRStatus(p, pos);
                    const lp = getLinkedPo(p);
                    return (
                      <tr key={`${p.purchaseRequisition}-${p.item}`}>
                        <td className="mono">{p.purchaseRequisition || "—"}</td>
                        <td className="num">
                          {formatQuantity(getRemainingPRQuantity(p))}
                        </td>
                        <td>{formatDate(p.deliveryDate)}</td>
                        <td>
                          <span className={`bg ${getPRBadgeClass(status)}`}>
                            {getPRBadgeText(status)}
                          </span>
                        </td>
                        <td className="mono dim">
                          {lp ? lp.purchasingDocument : "—"}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="empty">
                      No requisitions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <div className="card-h">
            <div className="card-t">Purchase orders</div>
          </div>
          <div className="tw">
            <table className="dt">
              <thead>
                <tr>
                  <th>PO</th>
                  <th>Vendor</th>
                  <th className="r">Ordered</th>
                  <th className="r">Received</th>
                  <th>Delivery</th>
                </tr>
              </thead>
              <tbody>
                {materialPos.length > 0 ? (
                  materialPos.map((o) => {
                    const state = getPOStatus(o, today);
                    return (
                      <tr key={`${o.purchasingDocument}-${o.item}`}>
                        <td className="mono">{o.purchasingDocument || "—"}</td>
                        <td>{o.vendorName || "—"}</td>
                        <td className="num">
                          {formatQuantity(o.orderQuantity)}
                        </td>
                        <td className="num">
                          {formatQuantity(o.quantityDelivered)}
                        </td>
                        <td>
                          {o.deliveryDate ? formatDate(o.deliveryDate) : "No delivery date"}
                          {o.deliveryDateBasis === "LEAD_TIME" && (
                            <span className="dim"> (lead time)</span>
                          )}{" "}
                          <span className={`bg ${getPOBadgeClass(state)}`}>
                            {o.closedInSource ? "Closed" : getPOBadgeText(state)}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="empty">
                      Nothing on order.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialDetail;
